import { KeyValuePair } from "../types/interfaces";
import settings from "./settings.json";

export interface StorageConfig {
  sessionStorageExpiresInMinutes: number;
  localStorageExpiresInDays: number;
}

// export type Language = 'en' | 'es';
export interface LanguageOption {
  name: string;
  description: string;
  iconLabel: string;
  iconFile: string;
}

export interface LanguageConfig {
  defaultLanguageName: string;
  languages: LanguageOption[];
}

export interface OidcConfig {
  clientId: string;
  baseUrl: string;
  issuer: string;
  redirectUri: string;
  scopes: string[];
  pkce?: boolean;
  codeChallenge: string;
  challengeMethod: string;
  state: string;
  autoRenew?: boolean;
  tokenStorage?: string;
}

export interface SMART_Config {
  clientId: string;
  scope: string;
}

export interface TokenEndpointConfig {
  url: string;
  auth: string;
  payload: KeyValuePair<string>[];
}

export interface ApiConfig {
  baseUrl: string;
  userEndpoint: string;
  accountEndpoint: string;
  schedulingEndpoint: string;
  virtualWaitingRoomContextEndpoint: string;
  virtualWaitingRoomDetailsEndpoint: string;
  visitsEndpoint: string;
  enumOptionsEndPoint: string;
  locationDetailsEndPoint: string;
  skipActivationCodeValidation?: boolean;
  healthBlogInfoEndpoint: string;
  pageContentsEndpoint: string;
}

export interface UrlConfig {
  root: string;
  createAccount: string;
  instantActivation: string;
  linkExpired: string;
  emailVerification: string;
  resetPasswordCallback: string;
  resetPassword: string;
  changePassword: string;
  mfaUpdate: string;
  mfaEnrollment: string;
  biometrics: string;
  myChartEnrollment: string;
  registrationConfirmation: string;
  home: string;
  patientHome: string;
  scheduling: string;
  quickcareScheduling: string;
  schedulingGuide: string;
  getCareNow: string;

  prescriptions: string;
  urgentCare: string;
  virtualUrgentCare: string;
  virtualWaitingRoom: string;
  goHealth: string;
  goHealthUcUrl: string;
  termsOfUse: string;
  privacyPolicy: string;
  resendCode: string;
  login: string;
  loginHelp: string;
  loginWithRedirect: string;
  loginCallback: string;
  logout: string;
  logoutCallback: string;
  profile: string;
  personalInformation: string;
  contactInformation: string;
  demographicInformation: string;
  imageScheduling: string;
  screeningScheduling: string;
  therapyScheduling: string;
  programsScheduling: string;
  mhProgramsAndClasses: string;
  supportRoute: string;
  supportUrl: string;

  blockItUrl: string;
  blockItQuickCareUrl: string;
  resetBiometrics: string;
  securitySettings: string;
  epicSignUp: string;
  epicAccessCheck: string;

  gotoMychartLogin: string;
  gotoMychartHome: string;
  myChartHomeUrl: string;
  myChartLoginUrl: string;
  myChartLoginDeepLink: string;
  myChartHomeDeepLink: string;

  gotoTestResult: string;
  myChartTestResults: string;
  myChartTestResultsDeepLink: string;

  payAsGuest: string;
  mhmgBillPay: string;
  mhHospitalsBillPay: string;
  myChartGuestBillPay: string;

  payBill: string;
  myChartPayBill: string;
  myChartPayBillDeeplink: string;

  guestCostEstimate: string;
  myChartGuestCostEstimate: string;

  costEstimate: string;
  myChartCostEstimate: string;
  myChartCostEstimateDeeplink: string;

  tsBariatrics: string;
  tsCardiology: string;
  tsEndocrinology: string;
  tsGastroenterology: string;
  tsGeneralSurgery: string;
  tsNeurology: string;
  tsPediatricsWellChild: string;
  tsPediatricsBHV: string;
  tsUrology: string;
  tsFamilyMedicine: string;
  tsOBGYN: string;
  tsAllergyImmunology: string;
  tsInfectiousDisease: string;
  tsRheumatology: string;

  functionIsComing: string;
  visits: string;
  blog: string;
  surveyRoute: string;
  surveyLink: string;

  valeHealthUrl: string;
  mealServiceRoute: string;
  mealServiceUrl: string;
}

export interface PageNames {
  home: string;
  createAccount: string;
  getCareNow: string;
  scheduling: string;
  urgentCare: string;
  virtualUrgentCare: string;
  accountSettings: string;
  personalInformation: string;
  contactInformation: string;
  demographicInformation: string;
}

// export interface BlockitConfig {
//   baseUrl: string;
//   fuzzySearchEndpoint: string;
//   redirectUrl: string;
// }

export interface ProfileFieldAttributes {
  name: string;
  label: string;
}
export interface AddressFieldsAttributes {
  name: string;
  label: string;
  addressLine1: ProfileFieldAttributes;
  addressLine2: ProfileFieldAttributes;
  city: ProfileFieldAttributes;
  state: ProfileFieldAttributes;
  postalCode: ProfileFieldAttributes;
}
export interface ProfileFields {
  firstName: ProfileFieldAttributes;
  lastName: ProfileFieldAttributes;
  dateOfBirth: ProfileFieldAttributes;
  legalSex: ProfileFieldAttributes;
  preferredFirstName: ProfileFieldAttributes;
  preferredPronouns: ProfileFieldAttributes;
  language: ProfileFieldAttributes;
  address: AddressFieldsAttributes;
  email: ProfileFieldAttributes;
  mobile: ProfileFieldAttributes;
  race: ProfileFieldAttributes;
  ethnicity: ProfileFieldAttributes;
  genderIdentity: ProfileFieldAttributes;
  sexualOrientation: ProfileFieldAttributes;
  maritalStatus: ProfileFieldAttributes;
  religion: ProfileFieldAttributes;
}

export interface CachingConfig {
  formData: boolean;
}

export interface AppConfig {
  disableBrowserView?: boolean | null;
}

const config: {
  env: string;
  guestOnly: boolean;
  disableSelfEnrollment: boolean;
  disableBiometrics: boolean;
  appconfig: AppConfig;
  cachingConfig: CachingConfig;
  storageConfig: StorageConfig;
  // oidc: OidcConfig;
  smart: SMART_Config;
  tokenEndpoint: TokenEndpointConfig;
  urlConfig: UrlConfig;
  apiConfig: ApiConfig;
  languageConfig: LanguageConfig;
  // blockitConfig: BlockitConfig;
  pageNames: PageNames;
  profileFields: ProfileFields;
} = {
  env: import.meta.env.VITE_APP_ENVIRONMENT || 'Unspecified',
  guestOnly:
    `${import.meta.env.VITE_APP_GUESTONLY || ''}`.trim().toLowerCase() == 'true',
  disableSelfEnrollment:
    `${import.meta.env.VITE_APP_DISABLE_SELF_ENROLLMENT || ''}`
      .trim()
      .toLowerCase() == 'true',

  disableBiometrics:
    `${import.meta.env.VITE_APP_DISABLE_BIOMETRICS || ''}`.trim().toLowerCase() ==
    'true',
  appconfig: {
    ...settings.appConfig,
    disableBrowserView: import.meta.env.VITE_APP_DISABLE_BROWSER_VIEW
      ? import.meta.env.VITE_APP_DISABLE_BROWSER_VIEW == 'true'
      : undefined,
  },
  cachingConfig: {
    ...settings.cachingConfig,
  },
  storageConfig: {
    ...settings.storageConfig,
    sessionStorageExpiresInMinutes: import.meta.env
      .VITE_APP_SESSION_STORAGE_EXPIRES_MINUTES
      ? Number(import.meta.env.VITE_APP_SESSION_STORAGE_EXPIRES_MINUTES)
      : settings.storageConfig.sessionStorageExpiresInMinutes,
    localStorageExpiresInDays: import.meta.env.VITE_APP_LOCAL_STORAGE_EXPIRES_DAYS
      ? Number(import.meta.env.VITE_APP_LOCAL_STORAGE_EXPIRES_DAYS)
      : settings.storageConfig.localStorageExpiresInDays,
  },
  // oidc: {
  //   ...settings.oidc,
  //   baseUrl: import.meta.env.VITE_APP_OIDC_BASE_URL || settings.oidc.baseUrl,
  //   redirectUri: `${window.location.origin}${settings.oidc.redirectUri}`,
  //   clientId: import.meta.env.VITE_APP_OIDC_CLIENT_ID || settings.oidc.clientId,
  //   issuer:
  //     import.meta.env.VITE_APP_OIDC_BASE_URL &&
  //     import.meta.env.VITE_APP_OIDC_ISSUER_ENDPOINT
  //       ? `${import.meta.env.VITE_APP_OIDC_BASE_URL}${import.meta.env.VITE_APP_OIDC_ISSUER_ENDPOINT}`
  //       : settings.oidc.issuer,
  // },
  smart: {
    ...settings.smart,
    clientId: import.meta.env.VITE_APP_EPIC_FHIR_CLIENT_ID || settings.smart.clientId,
  },
  tokenEndpoint: {
    ...settings.tokenEndpoint,
    url:
      import.meta.env.VITE_APP_OIDC_BASE_URL &&
        import.meta.env.VITE_APP_TOKEN_ENDPOINT
        ? `${import.meta.env.VITE_APP_OIDC_BASE_URL}${import.meta.env.VITE_APP_TOKEN_ENDPOINT}`
        : settings.tokenEndpoint.url,
    auth:
      import.meta.env.VITE_APP_TOKEN_ENDPOINT_AUTH || settings.tokenEndpoint.auth,
  },
  urlConfig: {
    ...settings.urlConfig,
    myChartHomeUrl:
      import.meta.env.VITE_APP_MYCHART_HOME ||
      settings.urlConfig.myChartHomeUrl,
    myChartLoginUrl: import.meta.env.VITE_APP_MYCHART_SIGNIN ||
      settings.urlConfig.myChartLoginUrl,
    myChartLoginDeepLink:
      import.meta.env.VITE_APP_MYCHART_SIGNIN_DEEPLINK ||
      settings.urlConfig.myChartLoginDeepLink,
    myChartHomeDeepLink:
      import.meta.env.VITE_APP_MYCHART_HOME_DEEPLINK ||
      settings.urlConfig.myChartHomeDeepLink,
    myChartTestResults:
      import.meta.env.VITE_APP_MYCHART_TEST_RESULT ||
      settings.urlConfig.myChartTestResults,
    myChartTestResultsDeepLink:
      import.meta.env.VITE_APP_MYCHART_TEST_RESULT_DEEPLINK ||
      settings.urlConfig.myChartTestResultsDeepLink,

    myChartGuestBillPay:
      import.meta.env.VITE_APP_MYCHART_GUEST_PAY ||
      settings.urlConfig.myChartGuestBillPay,
    myChartPayBill:
      import.meta.env.VITE_APP_MYCHART_PAY ||
      settings.urlConfig.myChartPayBill,
    myChartPayBillDeeplink:
      import.meta.env.VITE_APP_MYCHART_PAY_DEEPLINK ||
      settings.urlConfig.myChartPayBillDeeplink,

    myChartGuestCostEstimate:
      import.meta.env.VITE_APP_MYCHART_GUEST_ESTIMATE ||
      settings.urlConfig.myChartGuestCostEstimate,
    myChartCostEstimate:
      import.meta.env.VITE_APP_MYCHART_ESTIMATE ||
      settings.urlConfig.myChartCostEstimate,
    myChartCostEstimateDeeplink:
      import.meta.env.VITE_APP_MYCHART_ESTIMATE_DEEPLINK ||
      settings.urlConfig.myChartCostEstimateDeeplink,

    blockItUrl:
      import.meta.env.VITE_APP_BLCOKIT_URL || settings.urlConfig.blockItUrl,
    blockItQuickCareUrl:
      import.meta.env.VITE_APP_BLOCKIT_QUICKCARE_URL ||
      settings.urlConfig.blockItQuickCareUrl,
    valeHealthUrl: import.meta.env.VITE_APP_VALE_URL || settings.urlConfig.valeHealthUrl,
    mealServiceUrl: import.meta.env.MEAL_SERVICE_URL || settings.urlConfig.mealServiceUrl
  },
  apiConfig: {
    ...settings.apiConfig,
    baseUrl: import.meta.env.VITE_APP_API_URL || settings.apiConfig.baseUrl,
  },
  languageConfig: settings.languageConfig,
  // blockitConfig: {
  //   ...settings.blockitConfig,
  //   baseUrl:
  //     import.meta.env.VITE_APP_BLCOKIT_URL || settings.blockitConfig.baseUrl,
  //   redirectUrl:
  //     import.meta.env.VITE_APP_BLOCKIT_QUICKCARE_URL ||
  //     settings.blockitConfig.redirectUrl,
  // },
  pageNames: settings.pageNames,
  profileFields: settings.profileFields,
};

export default config;
